import React, { useEffect, useRef } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * SideDrawer — แผ่นเลื่อนจากขวา ใช้ดู/แก้รายการโดยไม่ออกจากตาราง
 * (เปิดหน้าใหม่ = เสียตำแหน่งในคิว)
 *
 * focus trap · คืนโฟกัสให้ตัวที่เปิด · ปิดด้วย Esc (ปิดได้เว้นแต่ dismissible=false)
 */
export interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** ข้อความรองใต้ชื่อ เช่น รหัสคำขอ */
  subtitle?: string;
  width?: 'md' | 'lg';
  /** ปุ่มด้านล่างที่ติดขอบ */
  footer?: React.ReactNode;
  /** false = ปิดด้วย Esc หรือคลิกฉากหลังไม่ได้ (งานที่กดยืนยันแล้วย้อนไม่ได้) */
  dismissible?: boolean;
  children: React.ReactNode;
}

export const SideDrawer: React.FC<SideDrawerProps> = ({
  open, onClose, title, subtitle, width = 'md', footer, dismissible = true, children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  /**
   * ⚠️ เก็บ onClose ไว้ใน ref แทนที่จะใส่ใน dependency ของ useEffect
   *
   * ผู้เรียกเกือบทุกที่ส่งฟังก์ชันแบบเขียนสด `onClose={() => setEdit(null)}`
   * ซึ่งเป็นคนละ reference ทุกครั้งที่ re-render ถ้าใส่ไว้ใน dependency
   * effect นี้จะ cleanup แล้วรันใหม่ **ทุกครั้งที่ผู้ใช้พิมพ์ตัวอักษรหนึ่งตัว**
   * ผลคือโฟกัสกระเด็นออกจากช่องที่กำลังพิมพ์ตั้งแต่ตัวอักษรแรก
   *
   * (เจอจริง: หน้าแก้ไขสิทธิพิเศษ พิมพ์ชื่อได้ทีละตัวแล้วเด้งไปปุ่มปิดทุกครั้ง)
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement as HTMLElement;
    const panel = panelRef.current;

    /**
     * ⚠️ ต้องหา [data-autofocus] แยกก่อน ห้ามรวมไว้ใน querySelector เดียว
     *
     * querySelector ที่มีหลาย selector คั่นด้วยจุลภาค คืน element **ตัวแรกตามลำดับ
     * ในเอกสาร** ที่ตรงกับอันใดอันหนึ่ง ไม่ใช่ตัวแรกที่ตรงกับ selector ตัวแรก
     * ปุ่มปิดอยู่ในหัวแผงซึ่งมาก่อนเนื้อหา ปุ่มปิดจึงชนะ [data-autofocus] เสมอ
     */
    const target = panel?.querySelector<HTMLElement>('[data-autofocus]')
      ?? panel?.querySelector<HTMLElement>('button, [href], input, select, textarea');
    target?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) { e.stopPropagation(); onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables.length) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      opener.current?.focus();
    };
    // ตั้งใจไม่ใส่ onClose ใน dependency — อ่านผ่าน onCloseRef แทน (ดูเหตุผลข้างบน)
  }, [open, dismissible]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[var(--z-overlay)] bg-[var(--scrim)]"
        onClick={dismissible ? onClose : undefined}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'fixed right-0 top-0 z-[var(--z-drawer)] flex h-full flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-drawer)]',
          width === 'lg' ? 'w-[var(--drawer-width-lg)]' : 'w-[var(--drawer-width)]',
          'max-w-[92vw]',
        )}
      >
        <header className="flex items-start gap-[var(--space-2)] border-b border-[var(--line-200)] px-[var(--space-4)] py-[var(--space-25)]">
          <div className="min-w-0 flex-1">
            <h2 className="bo-truncate text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
              {title}
            </h2>
            {subtitle && (
              <p className="bo-truncate text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{subtitle}</p>
            )}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="ปิด (Esc)"
              className="inline-flex h-[var(--hit-control)] w-[var(--hit-control)] items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-600)] hover:bg-[var(--surface-100)] hover:text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <Icon name="close" size={20} />
            </button>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-[var(--space-4)]">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-[var(--space-2)] border-t border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-25)]">
            {footer}
          </footer>
        )}
      </div>
    </>
  );
};
